import React, { useEffect, useRef } from 'react';
import './StartChecklistModal.css';
import { ChecklistType, checklistTypeLabel } from '../../../types/checklist';
import { tipoDeEntradaDoAtendimento, tipoDeSaidaDoAtendimento } from '../../../shared/checklist-familias';

/**
 * at-96: o tipo continua se chamando `TipoServico` (é o nome do campo no corpo da requisição),
 * mas o conjunto é o do `ChecklistType` — `REPARO`/`RETORNO` saíram e os dois de pós-evento
 * entraram.
 */
export type TipoServico = ChecklistType;

interface StartChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (tipoServico: TipoServico) => void;
  /**
   * at-96: o que o chamador está iniciando — a vistoria de ENTRADA ou a de SAÍDA.
   *
   * Substitui `defaultTipoServico` + `hideSaidaOption`: o tipo concreto é DERIVADO daqui com
   * `ehPosEvento`, não escolhido pelo usuário.
   */
  familia: 'ENTRADA' | 'SAIDA';
  /** at-96: o atendimento é um retorno de garantia (vem do `posEvento` da resposta do veículo). */
  ehPosEvento?: boolean;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
}

/**
 * Confirmação de início de checklist.
 *
 * 🔴 **O tipo é DERIVADO do atendimento, não escolhido** (at-96, decisão do Victor). Até aqui
 * era um `select` de 4 opções, e isso permitia fazer um checklist de entrada normal num
 * pós-evento em silêncio — com o erro aparecendo só no PDF que chega ao associado.
 *
 * A tela mostra qual checklist vai ser feito e **por quê**; a imposição é do backend, que
 * recusa o par errado com `409 CHECKLIST_TIPO_INCOMPATIVEL`.
 */
const StartChecklistModal: React.FC<StartChecklistModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  familia,
  ehPosEvento = false,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const tipoServico: TipoServico =
    familia === 'SAIDA' ? tipoDeSaidaDoAtendimento(ehPosEvento) : tipoDeEntradaDoAtendimento(ehPosEvento);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, sidebarRef]);

  if (!isOpen) return null;

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(tipoServico);
  };

  return (
    <div className="status-modal-overlay">
      <div className="status-modal-content" ref={modalRef}>
        <div className="LightboxStatus">
          <form className="LightboxStatusForm">
            <div className="EditarCarro">Tipo de Serviço</div>

            <div className="CampoStatus">
              <p className="start-checklist-tipo">{checklistTypeLabel[tipoServico]}</p>
              {/* A hint explica a REGRA, não o clique: por que este é o tipo, e não outro. */}
              <p className="start-checklist-hint">
                {ehPosEvento
                  ? 'Este atendimento é um retorno de garantia, então o checklist é o de pós-evento. O tipo não é escolhido: ele acompanha o atendimento.'
                  : 'O tipo do checklist acompanha o atendimento e não é escolhido aqui.'}
              </p>
            </div>

            <div className="Frame1000003998Status">
              <div className="CtaBigEditar cancel" onClick={onClose}>
                <button className="CustomFileCancel" type="button">
                  Cancelar
                </button>
              </div>

              <button
                onClick={handleConfirm}
                className="CtaBigEditar save"
                type="button"
              >
                Confirmar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StartChecklistModal;
